import { useEffect, useRef, useState, useCallback, type ElementType } from 'react'
import maplibregl from 'maplibre-gl'
import {
  ZoomIn, ZoomOut, Maximize2, LayoutGrid, MapPin, Cpu, Tag, X,
  AlertTriangle, Activity, BarChart2, Sun, Edit3, BoxSelect,
  Camera, Package, Building2, Shield, Home, Navigation, Cloud, Gauge, Zap,
} from 'lucide-react'
import type { PlantDataset, TrackerData, TrackerStatus, TrackerStepCode } from '@/types/plant'
import {
  TRACKER_STATUS_META, PART_NO_CONFIG,
  TRACKER_STEPS, trackerStepStartPct, activeTrackerStepIdx,
  PILE_STEPS,
} from '@/types/plant'
import { getStepsForElementKey, type StepConfig } from '@/lib/layerRegistry'
import {
  loadPlantData,
  buildTrackerGeoJSON,
  buildPilesGeoJSON,
  saveTrackerOverride,
  savePileStatusOverride,
  batchSaveTrackerOverrides,
  batchSavePileStatusOverrides,
  loadGenericStepOverrides,
  saveGenericStepOverride,
} from '@/lib/plantParser'
import { syncFromSupabase, persistToSupabase } from '@/lib/elementStepProgress'
import { QuickEditPopover, type QuickEditState } from '@/components/layout/QuickEditPopover'
import { GenericElementPanel } from '@/components/layout/GenericElementPanel'

// =============================================================================
// Constants
// =============================================================================

const DARK_STYLE     = 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json'
const DBLCLICK_DELAY = 250

// Status-based color expression shared by all generic element layers
const GENERIC_STATUS_COLOR = [
  'match', ['get', 'status'],
  'EN_CURSO',   '#F59E0B',
  'FINALIZADA', '#22C55E',
  'BLOQUEADA',  '#EF4444',
  '#475569',    // NO_INICIADA / default
] as unknown as maplibregl.ExpressionSpecification

// Line-width expressions scaled to real-world dimensions (lat ≈ 42°, Spain)
// Formula: pixels = N_metres * 2^zoom / 116345
const ZANJA_LINE_WIDTH: maplibregl.ExpressionSpecification = [
  'interpolate', ['exponential', 2], ['zoom'],
  14, 0.5, 16, 0.6, 17, 1.1, 18, 2.3, 19, 4.5, 20, 9.0,
] as unknown as maplibregl.ExpressionSpecification

const VIAL_LINE_WIDTH: maplibregl.ExpressionSpecification = [
  'interpolate', ['exponential', 2], ['zoom'],
  13, 0.5, 14, 0.6, 15, 1.1, 16, 2.3, 17, 4.5, 18, 9.0, 19, 18.0, 20, 36.0,
] as unknown as maplibregl.ExpressionSpecification

// Config for generic GeoJSON sources that need status-based coloring
const GENERIC_SOURCE_CONFIG = [
  { sourceId: 'zanja-bt',          elementKey: 'zanjas_bt',    idProp: 'tramId', url: '/data/zanja_bt_tramos.geojson'  },
  { sourceId: 'viales',            elementKey: 'viales',        idProp: 'tramId', url: '/data/viales_tramos.geojson'    },
  { sourceId: 'vallado-tramos',    elementKey: 'vallado',       idProp: 'tramId', url: '/data/vallado_tramos.geojson'   },
  { sourceId: 'inversores-string', elementKey: 'inversores',    idProp: 'handle', url: '/data/inversores_string.geojson'},
  { sourceId: 'subestaciones',     elementKey: 'subestaciones', idProp: 'handle', url: '/data/subestaciones.geojson'   },
] as const

const STATUS_LINE_COLOR = [
  'match', ['get', 'status'],
  'NO_INICIADA', '#475569',
  'EN_CURSO',    '#F59E0B',
  'FINALIZADA',  '#22C55E',
  'BLOQUEADA',   '#EF4444',
  '#475569',
] as unknown as maplibregl.ExpressionSpecification

// Pile color = based on overallStatus (installation progress)
const PILE_STATUS_COLOR = [
  'match', ['get', 'overallStatus'],
  'NO_INICIADA', '#374151',
  'EN_CURSO',    '#D97706',
  'FINALIZADA',  '#16A34A',
  'BLOQUEADA',   '#DC2626',
  '#374151',
] as unknown as maplibregl.ExpressionSpecification

// Motor pier fill color — grey when not started (same as regular piles), coloured when active
const MOTOR_STATUS_COLOR = [
  'match', ['get', 'overallStatus'],
  'NO_INICIADA', '#374151',   // grey — not started yet
  'EN_CURSO',    '#D97706',   // amber
  'FINALIZADA',  '#15803D',   // green
  'BLOQUEADA',   '#B91C1C',   // red
  '#374151',
] as unknown as maplibregl.ExpressionSpecification

// =============================================================================
// Element type catalogue — all plant element types (loaded and pending)
// =============================================================================

interface ElementTypeConfig {
  key:          string
  label:        string
  sublabel:     string
  icon:         ElementType
  color:        string
  mapLayerIds:  string[]   // MapLibre layer IDs to show/hide
  queryIds:     string[]   // layer IDs for queryRenderedFeatures
  featureProp:  string     // GeoJSON property containing the element ID
  loaded:       boolean
  selectable:   boolean    // can be box-selected for batch edit
}

const ELEMENT_TYPES: ElementTypeConfig[] = [
  { key: 'trackers',       label: 'Trackers',            sublabel: 'Líneas de tracker',       icon: Activity,    color: '#F59E0B', mapLayerIds: ['trackers-glow','trackers-mid','trackers-line'], queryIds: ['trackers-line'],             featureProp: 'codigoTracker', loaded: true,  selectable: true },
  { key: 'hincas',         label: 'Hincas',              sublabel: 'Puntos de hinca',          icon: MapPin,      color: '#94A3B8', mapLayerIds: ['piles-circle','piles-damper'],                  queryIds: ['piles-circle','motor-piers'], featureProp: 'codigoHinca',   loaded: true,  selectable: true },
  { key: 'motoras',        label: 'Motoras',             sublabel: 'Hincas con motor',         icon: Cpu,         color: '#3B82F6', mapLayerIds: ['motor-piers'],                                  queryIds: ['motor-piers'],               featureProp: 'codigoHinca',   loaded: true,  selectable: true  },
  { key: 'labels',         label: 'Etiquetas',           sublabel: 'Códigos de tracker',       icon: Tag,         color: '#64748B', mapLayerIds: ['trackers-labels'],                              queryIds: [],                            featureProp: '',              loaded: true,  selectable: false },
  { key: 'zanjas_bt',      label: 'Zanjas BT',           sublabel: 'Canalización baja tensión', icon: Zap,        color: '#F97316', mapLayerIds: ['zanja-bt-line'],                                queryIds: ['zanja-bt-line'],             featureProp: 'tramId',        loaded: true,  selectable: true  },
  { key: 'zanjas_mt',      label: 'Zanjas MT',           sublabel: 'Canalización media tensión',icon: Zap,        color: '#EF4444', mapLayerIds: [],                                               queryIds: [],                            featureProp: 'id',            loaded: false, selectable: false },
  { key: 'zanjas_ssaa',    label: 'Zanjas SSAA',         sublabel: 'Servicios auxiliares',      icon: Zap,        color: '#8B5CF6', mapLayerIds: [],                                               queryIds: [],                            featureProp: 'id',            loaded: false, selectable: false },
  { key: 'inversores',     label: 'Inversores',          sublabel: 'Inversores string',         icon: Gauge,      color: '#06B6D4', mapLayerIds: ['inversores-string-points'],                     queryIds: ['inversores-string-points'],  featureProp: 'handle',        loaded: true,  selectable: true  },
  { key: 'combiner_boxes', label: 'Combiner Boxes',      sublabel: 'Cajas combinadoras',        icon: Package,    color: '#EC4899', mapLayerIds: [],                                               queryIds: [],                            featureProp: 'id',            loaded: false, selectable: false },
  { key: 'power_stations', label: 'Power Stations',      sublabel: 'Estaciones de potencia',    icon: Building2,  color: '#FBBF24', mapLayerIds: [],                                               queryIds: [],                            featureProp: 'id',            loaded: false, selectable: false },
  { key: 'cctv',           label: 'CCTV',                sublabel: 'Cámaras de seguridad',      icon: Camera,     color: '#06B6D4', mapLayerIds: [],                                               queryIds: [],                            featureProp: 'id',            loaded: false, selectable: false },
  { key: 'vallado',        label: 'Vallado',             sublabel: 'Tramos de valla física',    icon: Shield,     color: '#A3E635', mapLayerIds: ['vallado-tramos-line'],                           queryIds: ['vallado-tramos-line'],       featureProp: 'tramId',        loaded: true,  selectable: true  },
  { key: 'subestaciones',  label: 'Subestaciones',       sublabel: 'Centros de transformación', icon: Building2,  color: '#EF4444', mapLayerIds: ['subestaciones-fill','subestaciones-line'],       queryIds: ['subestaciones-fill'],        featureProp: 'handle',        loaded: true,  selectable: true  },
  { key: 'temporales',     label: 'Zona Temporales',     sublabel: 'Instalaciones temporales',  icon: Home,       color: '#D97706', mapLayerIds: [],                                               queryIds: [],                            featureProp: 'id',            loaded: false, selectable: false },
  { key: 'viales',         label: 'Viales',              sublabel: 'Caminos internos',          icon: Navigation, color: '#92400E', mapLayerIds: ['viales-line'],                                  queryIds: ['viales-line'],               featureProp: 'tramId',        loaded: true,  selectable: true  },
  { key: 'meteo',          label: 'Est. Meteorológicas', sublabel: 'Sensores meteorológicos',   icon: Cloud,      color: '#0EA5E9', mapLayerIds: [],                                               queryIds: [],                            featureProp: 'id',            loaded: false, selectable: false },
]

// =============================================================================
// Tracker Detail Panel
// =============================================================================

interface DetailPanelProps {
  tracker:           TrackerData
  onClose:           () => void
  onSave:            (codigoTracker: string, status: TrackerStatus, pct: number) => void
  onPileStatusChange:(codigoHinca: string, status: TrackerStatus) => void
}

function TrackerDetailPanel({ tracker, onClose, onSave, onPileStatusChange }: DetailPanelProps) {
  const [status,           setStatus]           = useState<TrackerStatus>(tracker.status)
  const [progressPct,      setProgressPct]      = useState(tracker.progressPct)
  const [saved,            setSaved]            = useState(false)
  const [openPileDropdown, setOpenPileDropdown] = useState<string | null>(null)
  const [pileActiveStep,   setPileActiveStep]   = useState<Record<string, string>>({})
  const hincaSectionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setStatus(tracker.status)
    setProgressPct(tracker.progressPct)
    setSaved(false)
    setOpenPileDropdown(null)
    setPileActiveStep({})
  }, [tracker.codigoTracker])

  // Close pile dropdown on click outside the hinca section
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (hincaSectionRef.current && !hincaSectionRef.current.contains(e.target as Node)) {
        setOpenPileDropdown(null)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const meta = TRACKER_STATUS_META[status]

  const handleSave = () => {
    onSave(tracker.codigoTracker, status, progressPct)
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  // Select a PILE_STEP for a hinca: marks it EN_CURSO (or FINALIZADA for last step)
  const handlePileStepSelect = (codigoHinca: string, stepCode: string) => {
    const isLast = stepCode === PILE_STEPS[PILE_STEPS.length - 1].code
    onPileStatusChange(codigoHinca, isLast ? 'FINALIZADA' : 'EN_CURSO')
    setPileActiveStep(prev => ({ ...prev, [codigoHinca]: stepCode }))
    setOpenPileDropdown(null)
  }

  const STATUS_DOT: Record<TrackerStatus, string> = {
    NO_INICIADA: '#374151',
    EN_CURSO:    '#D97706',
    FINALIZADA:  '#16A34A',
    BLOQUEADA:   '#DC2626',
  }

  return (
    <div className="flex flex-col h-full text-sm">
      <div className="flex items-center justify-between p-4 border-b border-slate-700 shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: meta.color }} />
          <span className="font-mono text-xs text-slate-300 truncate max-w-[180px]">{tracker.codigoTracker}</span>
        </div>
        <button onClick={onClose} className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Info grid */}
        <div className="grid grid-cols-2 gap-2">
          {[
            { label: 'Parcela',       value: `P${String(tracker.parcela).padStart(2, '0')}` },
            { label: 'Power Station', value: tracker.powerStation },
            { label: 'NCU',           value: tracker.ncu },
            { label: 'Strings',       value: tracker.strings },
            { label: 'Orden',         value: `#${tracker.orden}` },
            { label: 'Hincas',        value: tracker.numHincas },
          ].map(({ label, value }) => (
            <div key={label} className="bg-slate-800 rounded-lg p-2">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">{label}</p>
              <p className="font-semibold text-white text-xs">{value}</p>
            </div>
          ))}
        </div>

        {/* Progress */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-slate-400">Avance tracker</span>
            <span className="text-base font-bold text-white">{progressPct}%</span>
          </div>
          <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-500"
                 style={{ width: `${progressPct}%`, backgroundColor: meta.color, boxShadow: `0 0 6px ${meta.glowColor}` }} />
          </div>
          <input type="range" min={0} max={100} value={progressPct}
                 onChange={(e) => setProgressPct(Number(e.target.value))}
                 className="w-full mt-2 accent-amber-500" />
        </div>

        {/* Tracker steps (WBS 5.x) */}
        <div>
          <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Actividad de tracker</p>
          <div className="flex flex-col gap-1">
            {TRACKER_STEPS.map((step) => {
              const stepPct = trackerStepStartPct(step.code as TrackerStepCode)
              const isActive = progressPct >= stepPct && (
                status === 'EN_CURSO' || status === 'FINALIZADA'
                  ? true
                  : false
              ) && status !== 'NO_INICIADA'
              const activeIdx = activeTrackerStepIdx(status, progressPct)
              const stepIdx   = TRACKER_STEPS.findIndex(s => s.code === step.code)
              const isCurrentStep = activeIdx !== null && activeIdx !== -1 && stepIdx === activeIdx
              const isDone = activeIdx === -1 || (activeIdx !== null && stepIdx < activeIdx)
              return (
                <button
                  key={step.code}
                  onClick={() => {
                    const pct = trackerStepStartPct(step.code as TrackerStepCode)
                    setProgressPct(pct)
                    setStatus(pct === 0 ? 'NO_INICIADA' : 'EN_CURSO')
                  }}
                  className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[11px] font-medium border transition-all duration-150 text-left"
                  style={isCurrentStep
                    ? { backgroundColor: `${step.color}25`, borderColor: step.color, color: step.glowColor, boxShadow: `0 0 8px ${step.color}30` }
                    : isDone
                    ? { backgroundColor: '#16A34A20', borderColor: '#16A34A60', color: '#4ADE80' }
                    : { backgroundColor: 'transparent', borderColor: '#334155', color: '#64748B' }}
                >
                  <span className="text-[9px] opacity-60 w-6 shrink-0">{step.code}</span>
                  <span className="flex-1 truncate">{step.label}</span>
                  {isDone && <span className="text-green-400 text-[10px]">✓</span>}
                  {isCurrentStep && <span className="text-[9px] opacity-60">{Math.round(step.weight * 100)}%</span>}
                </button>
              )
            })}
          </div>
          {/* Quick status overrides */}
          <div className="flex gap-1.5 mt-2">
            {(['NO_INICIADA', 'BLOQUEADA', 'FINALIZADA'] as TrackerStatus[]).map((s) => {
              const m = TRACKER_STATUS_META[s]
              return (
                <button key={s} onClick={() => {
                  setStatus(s)
                  if (s === 'NO_INICIADA') setProgressPct(0)
                  if (s === 'FINALIZADA')  setProgressPct(100)
                }}
                  className={`flex-1 px-2 py-1 rounded-lg text-[10px] font-medium border transition-all duration-150
                    ${status === s ? 'text-white' : 'border-slate-700 text-slate-500 hover:border-slate-500 hover:text-slate-300'}`}
                  style={status === s ? { backgroundColor: `${m.color}25`, borderColor: m.color, color: m.glowColor } : undefined}>
                  {m.label}
                </button>
              )
            })}
          </div>
        </div>

        {/* Hinca grid — shows PILE_STEPS dropdown on click */}
        <div ref={hincaSectionRef}>
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-xs text-slate-400 uppercase tracking-wider">Hincas</p>
            <p className="text-[9px] text-slate-600">clic = ver pasos</p>
          </div>
          <div className="grid grid-cols-4 gap-1">
            {tracker.piles.map((p) => {
              const st = p.overallStatus ?? 'NO_INICIADA'
              const dotColor = STATUS_DOT[st]
              const isOpen = openPileDropdown === p.codigoHinca
              return (
                <button
                  key={p.posicion}
                  onClick={() => setOpenPileDropdown(prev => prev === p.codigoHinca ? null : p.codigoHinca)}
                  className={`flex flex-col items-center p-1.5 rounded border transition-all cursor-pointer
                    ${isOpen ? 'bg-slate-700 border-slate-500' : 'bg-slate-800 border-slate-700 hover:border-slate-500 active:scale-95'}`}
                  title={`${p.codigoHinca}\nEstado: ${st}\n${p.esMotora ? 'MOTORA' : p.tieneDamper ? 'DAMPER' : 'ESTÁNDAR'}`}
                >
                  <div className="w-2.5 h-2.5 rounded-full mb-1 transition-colors"
                       style={{ backgroundColor: dotColor, boxShadow: st !== 'NO_INICIADA' ? `0 0 5px ${dotColor}` : undefined }} />
                  <span className="text-[8px] text-slate-500 leading-none">H{String(p.posicion).padStart(2, '0')}</span>
                  <div className="flex gap-0.5 mt-0.5">
                    {p.esMotora    && <span className="text-[7px] text-blue-400 font-bold leading-none">M</span>}
                    {p.tieneDamper && <span className="text-[7px] text-yellow-400 font-bold leading-none">D</span>}
                  </div>
                </button>
              )
            })}
          </div>

          {/* Inline PILE_STEPS dropdown */}
          {openPileDropdown && (
            <div className="mt-2 bg-slate-800/80 border border-slate-700 rounded-lg p-2">
              <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1.5 truncate">
                {openPileDropdown.split('-').slice(-1)[0]} — Paso activo
              </p>
              <div className="space-y-0.5">
                {PILE_STEPS.map((step) => {
                  const isActive = pileActiveStep[openPileDropdown] === step.code
                  return (
                    <button
                      key={step.code}
                      onClick={() => handlePileStepSelect(openPileDropdown, step.code)}
                      className="w-full flex items-center gap-2 px-2 py-1 rounded text-[10px] transition-all text-left"
                      style={isActive
                        ? { backgroundColor: `${step.color}30`, border: `1px solid ${step.color}80`, color: step.glowColor }
                        : { backgroundColor: 'transparent', border: '1px solid transparent', color: '#64748B' }}
                    >
                      <div className="w-2 h-2 rounded-full shrink-0"
                           style={{ backgroundColor: isActive ? step.color : '#374151' }} />
                      <span className="text-[9px] opacity-60 w-6 shrink-0">{step.code}</span>
                      <span className="flex-1 truncate">{step.label}</span>
                      {isActive && <span className="text-[8px] opacity-60">✓</span>}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Hinca legend */}
          <div className="flex items-center gap-3 mt-2">
            <div className="flex items-center gap-1"><div className="w-1.5 h-1.5 rounded-full bg-[#374151]" /><span className="text-[9px] text-slate-600">No inic.</span></div>
            <div className="flex items-center gap-1"><div className="w-1.5 h-1.5 rounded-full bg-[#D97706]" /><span className="text-[9px] text-slate-600">En curso</span></div>
            <div className="flex items-center gap-1"><div className="w-1.5 h-1.5 rounded-full bg-[#16A34A]" /><span className="text-[9px] text-slate-600">Finalizada</span></div>
            <div className="flex items-center gap-1"><span className="text-[9px] text-blue-400 font-bold">M</span><span className="text-[9px] text-slate-600">Motora</span></div>
            <div className="flex items-center gap-1"><span className="text-[9px] text-yellow-400 font-bold">D</span><span className="text-[9px] text-slate-600">Damper</span></div>
          </div>
        </div>
      </div>

      {/* Save tracker */}
      <div className="p-4 border-t border-slate-700 shrink-0">
        <button onClick={handleSave}
                className={`w-full py-2.5 rounded-xl font-semibold text-sm transition-all duration-200
                  ${saved ? 'bg-green-600 text-white shadow-[0_0_20px_#22C55E60]'
                          : 'bg-amber-500 hover:bg-amber-400 text-slate-900 shadow-[0_0_20px_#F59E0B40]'}`}>
          {saved ? '✓ Guardado' : 'Guardar estado tracker'}
        </button>
      </div>
    </div>
  )
}

// =============================================================================
// Parcela stat bar
// =============================================================================
function ParcelaStatBar({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="flex items-center gap-2 text-[10px]">
      <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
      <span className="text-slate-400 w-16 truncate">{label}</span>
      <span className="font-semibold text-white">{value}</span>
    </div>
  )
}

// =============================================================================
// LayoutPage
// =============================================================================

export default function LayoutPage() {
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const mapRef          = useRef<maplibregl.Map | null>(null)
  const [mapReady,      setMapReady]      = useState(false)

  const [dataset,      setDataset]      = useState<PlantDataset | null>(null)
  const [loadProgress, setLoadProgress] = useState(0)
  const [loadError,    setLoadError]    = useState<string | null>(null)

  const [selectedParcela, setSelectedParcela] = useState<number | null>(null)
  const [selectedTracker, setSelectedTracker] = useState<TrackerData | null>(null)
  const [detailOpen,      setDetailOpen]      = useState(false)
  const [selectedGeneric, setSelectedGeneric] = useState<{
    layerKey:  string
    layerLabel: string
    featureId: string
    steps:     readonly StepConfig[]
    layerColor: string
    stepCode?: string
  } | null>(null)
  const [quickEdit,       setQuickEdit]       = useState<QuickEditState | null>(null)
  const [zoom,            setZoom]            = useState(13)

  // Visibility per element type key
  // Global view (no parcela): trackers + vallado visible; hincas/motoras hidden
  // Parcela view: all relevant layers shown (updated by selectedParcela useEffect)
  const [visibleElements, setVisibleElements] = useState<Record<string, boolean>>({
    trackers: true,
    hincas:   false,
    motoras:  false,
    labels:   false,
    vallado:  true,
  })

  // Ref to track selectedParcela inside MapLibre event handlers (avoids stale closure)
  const selectedParcelaRef = useRef<number | null>(null)
  // Active element type for box-selection targeting
  const [activeElement, setActiveElement] = useState<string | null>(null)

  // Box selection state
  const [selectionMode,      setSelectionMode]      = useState(false)
  const [selectionBox,       setSelectionBox]       = useState<{ x1: number; y1: number; x2: number; y2: number } | null>(null)
  const [selectedElementIds, setSelectedElementIds] = useState<Set<string>>(new Set())
  const [batchEditOpen,      setBatchEditOpen]      = useState(false)
  // Pixel position (relative to map container) where the batch bar should appear
  const [batchBarPos,        setBatchBarPos]        = useState<{ x: number; y: number } | null>(null)
  const selectionStartRef = useRef<{ x: number; y: number } | null>(null)
  const isDraggingRef     = useRef(false)

  // In-memory GeoJSON for generic sources — enriched with `status` property for color
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const genericGeoJSONRef = useRef<Record<string, any>>({})

  const hoveredIdRef              = useRef<number | null>(null)
  const selectedIdRef             = useRef<number | null>(null)
  const clickTimerRef             = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Stores MapLibre numeric feature IDs of box-selected trackers (for setFeatureState)
  const selectedTrackerMapIdsRef  = useRef<number[]>([])
  // Saves visibility state before layer isolation so we can restore it
  const preIsolationVisibilityRef = useRef<Record<string, boolean> | null>(null)

  // Batch processing indicator — prevents UI interaction and shows spinner
  const [isBatchProcessing, setIsBatchProcessing] = useState(false)

  const [mapSize, setMapSize] = useState({ w: 800, h: 600 })
  useEffect(() => {
    const el = mapContainerRef.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) => {
      setMapSize({ w: entry.contentRect.width, h: entry.contentRect.height })
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Keep ref in sync with state (for use inside MapLibre event handlers)
  useEffect(() => { selectedParcelaRef.current = selectedParcela }, [selectedParcela])

  // Load data
  useEffect(() => {
    loadPlantData((pct) => setLoadProgress(pct))
      .then(setDataset)
      .catch((err: Error) => setLoadError(err.message))
  }, [])

  // Enable/disable map drag pan based on selection mode
  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapReady) return
    if (selectionMode) {
      map.dragPan.disable()
      map.getCanvas().style.cursor = 'crosshair'
    } else {
      map.dragPan.enable()
      map.getCanvas().style.cursor = ''
    }
  }, [selectionMode, mapReady])

  // Init map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return
    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style:     DARK_STYLE,
      center:    [-3.85, 41.85],
      zoom:      11,
      attributionControl: false,
      antialias:          true,
    })
    map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-right')
    map.addControl(new maplibregl.ScaleControl({ unit: 'metric' }), 'bottom-left')

    map.on('load', () => {
      // Tracker lines
      map.addSource('trackers', { type: 'geojson', data: { type: 'FeatureCollection', features: [] }, generateId: true })
      map.addLayer({ id: 'trackers-glow', type: 'line', source: 'trackers',
        paint: { 'line-color': STATUS_LINE_COLOR,
                 'line-width': ['case', ['boolean', ['feature-state', 'selected'], false], 20, 12],
                 'line-opacity': 0.18, 'line-blur': 4 } })
      map.addLayer({ id: 'trackers-mid', type: 'line', source: 'trackers',
        paint: { 'line-color': STATUS_LINE_COLOR,
                 'line-width': ['case', ['boolean', ['feature-state', 'selected'], false], 8, 4],
                 'line-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 0.6, 0.35], 'line-blur': 2 } })
      map.addLayer({ id: 'trackers-line', type: 'line', source: 'trackers',
        paint: { 'line-color': STATUS_LINE_COLOR,
                 'line-width': ['case',
                   ['boolean', ['feature-state', 'selected'], false], 3.5,
                   ['boolean', ['feature-state', 'hover'],    false], 2.5, 1.5] } })
      map.addLayer({ id: 'trackers-labels', type: 'symbol', source: 'trackers', minzoom: 18,
        layout: { 'text-field': ['get', 'codigoTracker'], 'text-size': 9,
                  'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
                  'symbol-placement': 'line-center', 'text-rotation-alignment': 'map' },
        paint: { 'text-color': '#E2E8F0', 'text-halo-color': '#0F172A', 'text-halo-width': 1.5 } })

      // Piles
      map.addSource('piles', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })

      // Standard piles (non-motor)
      map.addLayer({ id: 'piles-circle', type: 'circle', source: 'piles', minzoom: 16,
        filter: ['!=', ['get', 'esMotora'], true],
        paint: {
          'circle-radius': 3,
          'circle-color': PILE_STATUS_COLOR,
          'circle-stroke-width': 0.5, 'circle-stroke-color': '#0F172A', 'circle-opacity': 0.9,
        } })

      // Damper ring indicator (golden ring on piles with dampers)
      map.addLayer({ id: 'piles-damper', type: 'circle', source: 'piles', minzoom: 15,
        filter: ['==', ['get', 'tieneDamper'], true],
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 15, 4, 18, 7, 20, 11],
          'circle-color': 'rgba(0,0,0,0)',
          'circle-stroke-width': 1.5,
          'circle-stroke-color': '#FCD34D',  // golden ring
          'circle-stroke-opacity': 0.9,
          'circle-opacity': 0,
        } })

      // Motor piers — colored by their overallStatus
      map.addLayer({ id: 'motor-piers', type: 'circle', source: 'piles', minzoom: 14,
        filter: ['==', ['get', 'esMotora'], true],
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 14, 4, 17, 8, 20, 14],
          'circle-color': MOTOR_STATUS_COLOR,
          'circle-stroke-width': 1.5, 'circle-stroke-color': '#1E40AF', 'circle-opacity': 1, 'circle-blur': 0.15,
        } })

      // ── Parcel perimeters (for navigation) ────────────────────────────────
      map.addSource('parcel-perimeters', { type: 'geojson', data: '/data/parcel_perimeters.geojson', generateId: true })
      map.addLayer({ id: 'parcel-perimeters-fill', type: 'fill', source: 'parcel-perimeters',
        paint: { 'fill-color': '#F59E0B', 'fill-opacity': 0.03 },
      })
      map.addLayer({ id: 'parcel-perimeters-line', type: 'line', source: 'parcel-perimeters',
        paint: { 'line-color': '#F59E0B', 'line-width': 1.5, 'line-opacity': 0.6, 'line-dasharray': ['literal', [4, 2]] },
      })

      // ── Vallado tramos (physical fence — 500m line segments, CAT 18) ─────
      map.addSource('vallado-tramos', { type: 'geojson', data: { type: 'FeatureCollection', features: [] }, generateId: true })
      map.addLayer({ id: 'vallado-tramos-line', type: 'line', source: 'vallado-tramos', layout: { visibility: 'none' },
        paint: { 'line-color': GENERIC_STATUS_COLOR, 'line-width': 2, 'line-opacity': 0.85 },
      })

      // ── Zanjas BT (~1 m real-world width, CAT 7) ──────────────────────────
      map.addSource('zanja-bt', { type: 'geojson', data: { type: 'FeatureCollection', features: [] }, generateId: true })
      map.addLayer({ id: 'zanja-bt-line', type: 'line', source: 'zanja-bt', layout: { visibility: 'none' },
        paint: { 'line-color': GENERIC_STATUS_COLOR, 'line-width': ZANJA_LINE_WIDTH, 'line-opacity': 0.9 },
      })

      // ── Viales (~4 m real-world width, CAT 19) ────────────────────────────
      map.addSource('viales', { type: 'geojson', data: { type: 'FeatureCollection', features: [] }, generateId: true })
      map.addLayer({ id: 'viales-line', type: 'line', source: 'viales', layout: { visibility: 'none' },
        paint: { 'line-color': GENERIC_STATUS_COLOR, 'line-width': VIAL_LINE_WIDTH, 'line-opacity': 0.85 },
      })

      // ── Inversores string (CAT 12) ─────────────────────────────────────────
      map.addSource('inversores-string', { type: 'geojson', data: { type: 'FeatureCollection', features: [] }, generateId: true })
      map.addLayer({ id: 'inversores-string-points', type: 'circle', source: 'inversores-string', layout: { visibility: 'none' },
        paint: { 'circle-color': GENERIC_STATUS_COLOR, 'circle-radius': 4, 'circle-stroke-color': '#0F172A', 'circle-stroke-width': 1 },
      })

      // ── Subestaciones (CAT 20) ─────────────────────────────────────────────
      map.addSource('subestaciones', { type: 'geojson', data: { type: 'FeatureCollection', features: [] }, generateId: true })
      map.addLayer({ id: 'subestaciones-fill', type: 'fill', source: 'subestaciones', layout: { visibility: 'visible' },
        paint: { 'fill-color': GENERIC_STATUS_COLOR, 'fill-opacity': 0.35 },
      })
      map.addLayer({ id: 'subestaciones-line', type: 'line', source: 'subestaciones', layout: { visibility: 'visible' },
        paint: { 'line-color': GENERIC_STATUS_COLOR, 'line-width': 2, 'line-opacity': 0.9 },
      })

      // Selection highlight source — populated by box-select for hincas
      map.addSource('piles-selected', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
      map.addLayer({
        id: 'piles-selected-ring', type: 'circle', source: 'piles-selected',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 14, 9, 17, 15, 20, 22],
          'circle-color': 'rgba(0,0,0,0)',
          'circle-stroke-width': 2.5,
          'circle-stroke-color': '#60A5FA',
          'circle-stroke-opacity': 1,
          'circle-opacity': 0,
        },
      })

      // Hover on trackers
      map.on('mousemove', 'trackers-line', (e) => {
        if (!selectionMode) map.getCanvas().style.cursor = 'pointer'
        const id = e.features?.[0]?.id
        if (id === undefined) return
        if (hoveredIdRef.current !== null && hoveredIdRef.current !== id) {
          map.setFeatureState({ source: 'trackers', id: hoveredIdRef.current }, { hover: false })
        }
        hoveredIdRef.current = id as number
        map.setFeatureState({ source: 'trackers', id }, { hover: true })
      })
      map.on('mouseleave', 'trackers-line', () => {
        if (!selectionMode) map.getCanvas().style.cursor = ''
        if (hoveredIdRef.current !== null) {
          map.setFeatureState({ source: 'trackers', id: hoveredIdRef.current }, { hover: false })
          hoveredIdRef.current = null
        }
      })
      map.on('mousemove', 'piles-circle', () => { if (!selectionMode) map.getCanvas().style.cursor = 'crosshair' })
      map.on('mousemove', 'motor-piers',  () => { if (!selectionMode) map.getCanvas().style.cursor = 'crosshair' })
      map.on('mouseleave', 'piles-circle', () => { if (!selectionMode) map.getCanvas().style.cursor = '' })
      map.on('mouseleave', 'motor-piers',  () => { if (!selectionMode) map.getCanvas().style.cursor = '' })

      // Hover cursor for generic element layers
      const GENERIC_CURSOR_LAYERS = ['zanja-bt-line', 'inversores-string-points', 'vallado-tramos-line', 'subestaciones-fill', 'viales-line']
      GENERIC_CURSOR_LAYERS.forEach(layerId => {
        map.on('mousemove', layerId, () => { if (!selectionMode) map.getCanvas().style.cursor = 'pointer' })
        map.on('mouseleave', layerId, () => { if (!selectionMode) map.getCanvas().style.cursor = '' })
      })

      // Parcel perimeter click — navigate to parcela from global view
      // Uses selectedParcelaRef to avoid stale closure
      map.on('click', 'parcel-perimeters-fill', (e) => {
        if (selectedParcelaRef.current !== null) return  // already in parcela view
        const parcela = e.features?.[0]?.properties?.parcela as number | undefined
        if (parcela) setSelectedParcela(parcela)
      })
      map.on('mousemove', 'parcel-perimeters-fill', () => {
        if (selectedParcelaRef.current === null) map.getCanvas().style.cursor = 'pointer'
      })
      map.on('mouseleave', 'parcel-perimeters-fill', () => {
        if (selectedParcelaRef.current === null) map.getCanvas().style.cursor = ''
      })

      map.on('zoom', () => setZoom(map.getZoom()))
      setMapReady(true)
    })

    mapRef.current = map
    return () => { map.remove(); mapRef.current = null }
  }, [])

  // Helper to rebuild piles GeoJSON for current parcela
  const rebuildPilesGeoJSON = useCallback(() => {
    if (!dataset || selectedParcela === null) return
    const trackers = dataset.trackersByParcela.get(selectedParcela) ?? []
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(mapRef.current?.getSource('piles') as maplibregl.GeoJSONSource)?.setData(buildPilesGeoJSON(trackers) as any)
  }, [dataset, selectedParcela])

  // Update map data when parcela selection changes (or dataset loads)
  // Global view (null): loads ALL trackers + shows all vallados
  // Parcela view (number): loads that parcela's trackers + filters vallado to that parcela
  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapReady || !dataset) return

    // Reset selection state first
    if (selectedIdRef.current !== null) {
      map.setFeatureState({ source: 'trackers', id: selectedIdRef.current }, { selected: false })
      selectedIdRef.current = null
    }
    preIsolationVisibilityRef.current = null
    setSelectedTracker(null)
    setDetailOpen(false)
    setSelectedGeneric(null)
    setQuickEdit(null)
    setSelectedElementIds(new Set())
    setBatchEditOpen(false)
    setBatchBarPos(null)
    setActiveElement(null)
    setSelectionMode(false)

    if (selectedParcela === null) {
      // ── GLOBAL PLANT VIEW ──────────────────────────────────────────────────
      // Load all trackers across all parcelas for the overview
      const allTrackers = [...dataset.trackersByParcela.values()].flat()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(map.getSource('trackers') as maplibregl.GeoJSONSource)?.setData(buildTrackerGeoJSON(allTrackers) as any)
      // Clear piles — too many to show globally (render per-parcela only)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(map.getSource('piles') as maplibregl.GeoJSONSource)?.setData({ type: 'FeatureCollection', features: [] } as any)
      // Remove parcela filter from perimeters — show all
      if (map.getLayer('parcel-perimeters-fill')) map.setFilter('parcel-perimeters-fill', null)
      if (map.getLayer('parcel-perimeters-line')) map.setFilter('parcel-perimeters-line', null)
      // Clear parcel filter on vallado — show all fences in global view
      if (map.getLayer('vallado-tramos-fill')) map.setFilter('vallado-tramos-fill', null)
      if (map.getLayer('vallado-tramos-line')) map.setFilter('vallado-tramos-line', null)
      // Hide per-parcela layers in global view
      const parcelaOnlyLayers = ['vallado-tramos-line','zanja-bt-line','viales-line','inversores-string-points']
      parcelaOnlyLayers.forEach(id => { if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', 'none') })
      // Fit to full plant extent
      map.fitBounds(
        [[dataset.bounds[0], dataset.bounds[1]], [dataset.bounds[2], dataset.bounds[3]]],
        { padding: 60, duration: 1000 }
      )
      // Global view: show trackers + vallado; hide piles (they need parcela context)
      setVisibleElements({ trackers: true, hincas: false, motoras: false, labels: false, vallado: true, subestaciones: true })
    } else {
      // ── PARCELA DETAIL VIEW ────────────────────────────────────────────────
      const trackers = dataset.trackersByParcela.get(selectedParcela) ?? []
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(map.getSource('trackers') as maplibregl.GeoJSONSource)?.setData(buildTrackerGeoJSON(trackers) as any)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(map.getSource('piles') as maplibregl.GeoJSONSource)?.setData(buildPilesGeoJSON(trackers) as any)
      // Filter perimeters to show only this parcela's outline
      const parcelaFilter = ['==', ['get', 'parcela'], selectedParcela] as unknown as maplibregl.ExpressionSpecification
      if (map.getLayer('parcel-perimeters-fill')) map.setFilter('parcel-perimeters-fill', parcelaFilter)
      if (map.getLayer('parcel-perimeters-line')) map.setFilter('parcel-perimeters-line', parcelaFilter)
      // Filter new layers to this parcela
      const newParcelaLayers = ['vallado-tramos-line','zanja-bt-line','viales-line','inversores-string-points']
      newParcelaLayers.forEach(id => {
        if (map.getLayer(id)) {
          map.setFilter(id, parcelaFilter)
          map.setLayoutProperty(id, 'visibility', 'visible')
        }
      })
      // Zoom to parcela bounds
      const stats = dataset.parcelaStats.get(selectedParcela)
      if (stats) map.fitBounds(
        [[stats.bounds[0], stats.bounds[1]], [stats.bounds[2], stats.bounds[3]]],
        { padding: 60, duration: 1000 }
      )
      // Parcela view: show trackers + piles + all new layers
      setVisibleElements({ trackers: true, hincas: true, motoras: true, labels: false, vallado: true, zanjas_bt: true, viales: true, inversores: true, subestaciones: true })
    }
  }, [selectedParcela, dataset, mapReady])

  // Click / dblclick handlers
  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapReady || !dataset) return

    const getTracker = (codigoTracker: string, parcela: number) => {
      const trackers = dataset.trackersByParcela.get(parcela) ?? []
      return trackers.find(t => t.codigoTracker === codigoTracker) ?? null
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onTrackerClick = (e: any) => {
      if (selectionMode) return
      const feature = e.features?.[0]
      if (!feature || clickTimerRef.current !== null) return
      clickTimerRef.current = setTimeout(() => {
        clickTimerRef.current = null
        const codigoTracker = feature.properties?.codigoTracker as string
        const featureId     = feature.id as number
        const parcela       = feature.properties?.parcela as number
        const tracker       = getTracker(codigoTracker, parcela)
        if (!tracker) return
        // In global view: navigate to parcela first, then show tracker detail
        if (selectedParcelaRef.current === null) {
          setSelectedParcela(parcela)
        }
        if (selectedIdRef.current !== null && selectedIdRef.current !== featureId) {
          map.setFeatureState({ source: 'trackers', id: selectedIdRef.current }, { selected: false })
        }
        selectedIdRef.current = featureId
        map.setFeatureState({ source: 'trackers', id: featureId }, { selected: true })
        setSelectedGeneric(null)
        setSelectedTracker(tracker)
        setDetailOpen(true)
        setQuickEdit(null)
      }, DBLCLICK_DELAY)
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onTrackerDblClick = (e: any) => {
      if (selectionMode) return
      e.preventDefault()
      if (clickTimerRef.current !== null) { clearTimeout(clickTimerRef.current); clickTimerRef.current = null }
      const feature = e.features?.[0]
      if (!feature) return
      const codigoTracker = feature.properties?.codigoTracker as string
      const tracker       = getTracker(codigoTracker, feature.properties?.parcela as number)
      if (!tracker) return
      setQuickEdit({ type: 'tracker', x: e.point.x, y: e.point.y, codigoTracker, currentStatus: tracker.status })
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onPileClick = (e: any) => {
      if (selectionMode) return
      const feature = e.features?.[0]
      if (!feature || clickTimerRef.current !== null) return
      clickTimerRef.current = setTimeout(() => {
        clickTimerRef.current = null
        const codigoTracker = feature.properties?.codigoTracker as string
        const tracker       = getTracker(codigoTracker, parseInt(String(feature.properties?.parcela ?? 0)))
        if (!tracker) return
        setSelectedTracker(tracker)
        setDetailOpen(true)
        setQuickEdit(null)
      }, DBLCLICK_DELAY)
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onPileDblClick = (e: any) => {
      if (selectionMode) return
      e.preventDefault()
      if (clickTimerRef.current !== null) { clearTimeout(clickTimerRef.current); clickTimerRef.current = null }
      const feature = e.features?.[0]
      if (!feature) return
      const codigoHinca   = feature.properties?.codigoHinca   as string
      const codigoTracker = feature.properties?.codigoTracker as string
      const posicion      = feature.properties?.posicion      as number
      const tracker       = getTracker(codigoTracker, parseInt(String(feature.properties?.parcela ?? 0)))
      const pile          = tracker?.piles.find(p => p.codigoHinca === codigoHinca)
      if (!pile) return
      setQuickEdit({
        type: 'pile', x: e.point.x, y: e.point.y,
        codigoTracker, codigoHinca, posicion,
        currentPartNo:  pile.partNo,
        currentStatus:  pile.overallStatus,
        esMotora:       pile.esMotora,
        tieneDamper:    pile.tieneDamper,
      })
    }

    const onMapClick = (e: maplibregl.MapMouseEvent) => {
      if (selectionMode) return
      const features = map.queryRenderedFeatures(e.point, {
        layers: ['trackers-line', 'piles-circle', 'motor-piers',
                 'zanja-bt-line', 'inversores-string-points', 'vallado-tramos-line', 'subestaciones-fill', 'viales-line'],
      })
      if (features.length === 0) {
        if (selectedIdRef.current !== null) {
          map.setFeatureState({ source: 'trackers', id: selectedIdRef.current }, { selected: false })
          selectedIdRef.current = null
        }
        setDetailOpen(false)
        setSelectedTracker(null)
        setSelectedGeneric(null)
        setQuickEdit(null)
      }
    }

    // Single-click → side panel, double-click → QuickEditPopover for generic element layers
    const GENERIC_LAYER_MAP: { layerId: string; elementKey: string; featureProp: string }[] = [
      { layerId: 'zanja-bt-line',            elementKey: 'zanjas_bt',     featureProp: 'tramId'  },
      { layerId: 'inversores-string-points', elementKey: 'inversores',    featureProp: 'handle'  },
      { layerId: 'vallado-tramos-line',      elementKey: 'vallado',       featureProp: 'tramId'  },
      { layerId: 'subestaciones-fill',       elementKey: 'subestaciones', featureProp: 'handle'  },
      { layerId: 'viales-line',              elementKey: 'viales',        featureProp: 'tramId'  },
    ]

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const genericHandlers: { layerId: string; singleFn: (e: any) => void; dblFn: (e: any) => void }[] = GENERIC_LAYER_MAP.map(({ layerId, elementKey, featureProp }) => {
      const et = ELEMENT_TYPES.find(e => e.key === elementKey)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const singleFn = (e: any) => {
        if (selectionMode) return
        const feature = e.features?.[0]
        if (!feature || clickTimerRef.current !== null) return
        clickTimerRef.current = setTimeout(() => {
          clickTimerRef.current = null
          const featureId = String(feature.properties?.[featureProp] ?? '')
          const steps = getStepsForElementKey(elementKey)
          const overrides = loadGenericStepOverrides()
          const stepCode = overrides[elementKey]?.[featureId]
          setSelectedTracker(null)
          setDetailOpen(false)
          setSelectedGeneric({
            layerKey:   elementKey,
            layerLabel: et?.label ?? elementKey,
            featureId,
            steps,
            layerColor: et?.color ?? '#94A3B8',
            stepCode,
          })
          setQuickEdit(null)
        }, DBLCLICK_DELAY)
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const dblFn = (e: any) => {
        if (selectionMode) return
        e.preventDefault()
        if (clickTimerRef.current !== null) { clearTimeout(clickTimerRef.current); clickTimerRef.current = null }
        const feature = e.features?.[0]
        if (!feature) return
        const featureId = String(feature.properties?.[featureProp] ?? '')
        const steps = getStepsForElementKey(elementKey)
        setQuickEdit({
          type: 'generic', x: e.point.x, y: e.point.y,
          codigoTracker: '',
          elementKey, featureId,
          featureLabel: featureId,
          steps,
        })
      }
      return { layerId, singleFn, dblFn }
    })

    genericHandlers.forEach(({ layerId, singleFn, dblFn }) => {
      if (map.getLayer(layerId)) {
        map.on('click',    layerId, singleFn)
        map.on('dblclick', layerId, dblFn)
      }
    })

    map.on('click',    'trackers-line', onTrackerClick)
    map.on('dblclick', 'trackers-line', onTrackerDblClick)
    map.on('click',    'piles-circle',  onPileClick)
    map.on('dblclick', 'piles-circle',  onPileDblClick)
    map.on('click',    'motor-piers',   onPileClick)
    map.on('dblclick', 'motor-piers',   onPileDblClick)
    map.on('click', onMapClick)

    return () => {
      genericHandlers.forEach(({ layerId, singleFn, dblFn }) => {
        if (map.getLayer(layerId)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          map.off('click',    layerId, singleFn as any)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          map.off('dblclick', layerId, dblFn    as any)
        }
      })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      map.off('click',    'trackers-line', onTrackerClick as any)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      map.off('dblclick', 'trackers-line', onTrackerDblClick as any)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      map.off('click',    'piles-circle',  onPileClick as any)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      map.off('dblclick', 'piles-circle',  onPileDblClick as any)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      map.off('click',    'motor-piers',   onPileClick as any)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      map.off('dblclick', 'motor-piers',   onPileDblClick as any)
      map.off('click', onMapClick)
    }
  }, [dataset, mapReady, selectionMode])

  // Layer visibility — driven by visibleElements keyed to ELEMENT_TYPES
  useEffect(() => {
    const map = mapRef.current
    if (!map || !mapReady) return
    const vis = (show: boolean): 'visible' | 'none' => show ? 'visible' : 'none'
    for (const et of ELEMENT_TYPES) {
      const show = visibleElements[et.key] ?? true
      for (const id of et.mapLayerIds) {
        if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', vis(show))
      }
    }
  }, [visibleElements, mapReady])

  // Load + enrich all generic GeoJSON sources with status (Supabase → localStorage → map)
  useEffect(() => {
    const map = mapRef.current
    if (!mapReady || !map) return
    const applyOverrides = () => {
      const overrides = loadGenericStepOverrides()
      GENERIC_SOURCE_CONFIG.forEach(({ sourceId, url, elementKey, idProp }) => {
        const steps = getStepsForElementKey(elementKey)
        const lastCode = steps[steps.length - 1]?.code
        fetch(url)
          .then(r => r.json())
          .then((geojson: { type: string; features: Array<{ properties: Record<string, unknown> }> }) => {
            geojson.features.forEach(f => {
              const id = String(f.properties?.[idProp] ?? '')
              const stepCode = overrides[elementKey]?.[id]
              f.properties.status = !stepCode ? 'NO_INICIADA' : stepCode === lastCode ? 'FINALIZADA' : 'EN_CURSO'
            })
            genericGeoJSONRef.current[sourceId] = geojson
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ;(map.getSource(sourceId) as maplibregl.GeoJSONSource)?.setData(geojson as any)
          })
          .catch(() => { /* source stays empty */ })
      })
    }
    // Pull latest from Supabase first, then apply (offline: falls back to localStorage)
    syncFromSupabase().then(applyOverrides).catch(applyOverrides)
  }, [mapReady])

  // Update a single feature's status in the in-memory GeoJSON and refresh the source
  const updateGenericFeatureStatus = useCallback((elementKey: string, featureId: string, stepCode: string | undefined) => {
    const map = mapRef.current
    if (!map) return
    const cfg = GENERIC_SOURCE_CONFIG.find(c => c.elementKey === elementKey)
    if (!cfg) return
    const geojson = genericGeoJSONRef.current[cfg.sourceId]
    if (!geojson) return
    const steps = getStepsForElementKey(elementKey)
    const lastCode = steps[steps.length - 1]?.code
    const status = !stepCode ? 'NO_INICIADA' : stepCode === lastCode ? 'FINALIZADA' : 'EN_CURSO'
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const feature = geojson.features.find((f: any) => String(f.properties?.[cfg.idProp] ?? '') === featureId)
    if (feature) {
      feature.properties.status = status
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(map.getSource(cfg.sourceId) as maplibregl.GeoJSONSource)?.setData(geojson as any)
    }
  }, [])

  // ---------------------------------------------------------------------------
  // Selection visual clear helper
  // ---------------------------------------------------------------------------
  const clearSelectionVisuals = useCallback(() => {
    const map = mapRef.current
    selectedTrackerMapIdsRef.current.forEach(id => {
      map?.setFeatureState({ source: 'trackers', id }, { selected: false })
    })
    selectedTrackerMapIdsRef.current = []
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(map?.getSource('piles-selected') as maplibregl.GeoJSONSource)?.setData({ type: 'FeatureCollection', features: [] } as any)
  }, [])

  // ---------------------------------------------------------------------------
  // Layer isolation helpers — show only one element type for clean selection
  // ---------------------------------------------------------------------------
  const isolateElement = useCallback((key: string) => {
    preIsolationVisibilityRef.current = { ...visibleElements }
    setActiveElement(key)
    const isolated: Record<string, boolean> = {}
    for (const et of ELEMENT_TYPES) { isolated[et.key] = et.key === key }
    setVisibleElements(isolated)
    setSelectionMode(true)
    setSelectedElementIds(new Set())
    setBatchEditOpen(false)
    clearSelectionVisuals()
  }, [visibleElements, clearSelectionVisuals])

  const deIsolate = useCallback(() => {
    if (preIsolationVisibilityRef.current) {
      setVisibleElements(preIsolationVisibilityRef.current)
      preIsolationVisibilityRef.current = null
    }
    setActiveElement(null)
    setSelectionMode(false)
    setSelectedElementIds(new Set())
    setBatchEditOpen(false)
    clearSelectionVisuals()
  }, [clearSelectionVisuals])

  // Tracker save
  const handleTrackerSave = useCallback((codigoTracker: string, status: TrackerStatus, progressPct: number) => {
    if (!dataset) return
    saveTrackerOverride(codigoTracker, status, progressPct)
    for (const trackers of dataset.trackersByParcela.values()) {
      const t = trackers.find(x => x.codigoTracker === codigoTracker)
      if (t) { t.status = status; t.progressPct = progressPct; break }
    }
    if (selectedParcela !== null) {
      const trackers = dataset.trackersByParcela.get(selectedParcela) ?? []
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(mapRef.current?.getSource('trackers') as maplibregl.GeoJSONSource)?.setData(buildTrackerGeoJSON(trackers) as any)
    }
    setSelectedTracker(prev => prev?.codigoTracker === codigoTracker ? { ...prev, status, progressPct } : prev)
  }, [dataset, selectedParcela])

  const handleTrackerStatusChange = useCallback((codigoTracker: string, status: TrackerStatus) => {
    if (!dataset) return
    let currentPct = 0
    for (const trackers of dataset.trackersByParcela.values()) {
      const t = trackers.find(x => x.codigoTracker === codigoTracker)
      if (t) { currentPct = t.progressPct; break }
    }
    handleTrackerSave(codigoTracker, status, currentPct)
    setSelectedTracker(prev => prev?.codigoTracker === codigoTracker ? { ...prev, status } : prev)
  }, [dataset, handleTrackerSave])

  // Generic element save — persists WBS step to localStorage and syncs panel state + map color
  const handleGenericSave = useCallback((layerKey: string, featureId: string, stepCode: string) => {
    saveGenericStepOverride(layerKey, featureId, stepCode)
    persistToSupabase(layerKey, featureId, stepCode)
    setSelectedGeneric(prev => prev ? { ...prev, stepCode } : null)
    updateGenericFeatureStatus(layerKey, featureId, stepCode)
  }, [updateGenericFeatureStatus])

  // Pile status change (from hinca grid click or quick edit)
  const handlePileStatusChange = useCallback((codigoHinca: string, status: TrackerStatus) => {
    if (!dataset || selectedParcela === null) return
    const trackers = dataset.trackersByParcela.get(selectedParcela) ?? []
    for (const tracker of trackers) {
      const pile = tracker.piles.find(p => p.codigoHinca === codigoHinca)
      if (pile) {
        pile.overallStatus = status
        // Force re-render of detail panel
        setSelectedTracker(prev => prev?.codigoTracker === tracker.codigoTracker ? { ...prev, piles: [...prev.piles] } : prev)
        break
      }
    }
    savePileStatusOverride(codigoHinca, status)
    rebuildPilesGeoJSON()
  }, [dataset, selectedParcela, rebuildPilesGeoJSON])

  // Batch status change — single localStorage write + single GeoJSON rebuild
  // Previously called handlePileStatusChange/handleTrackerSave N times per element,
  // triggering N×setData() on the MapLibre source and N localStorage read+writes,
  // which froze the main thread for large selections.
  const handleBatchStatusChange = useCallback((status: TrackerStatus) => {
    if (!dataset || selectedElementIds.size === 0) return
    setIsBatchProcessing(true)
    // Yield one frame so the processing indicator renders before the synchronous work
    setTimeout(() => {
      if (activeElement === 'hincas' || activeElement === 'motoras') {
        if (selectedParcela === null) { setIsBatchProcessing(false); return }
        const trackers = dataset.trackersByParcela.get(selectedParcela) ?? []
        const batchOverrides: Record<string, TrackerStatus> = {}
        // Mutate all in-memory objects in one pass
        for (const codigoHinca of selectedElementIds) {
          for (const tracker of trackers) {
            const pile = tracker.piles.find(p => p.codigoHinca === codigoHinca)
            if (pile) { pile.overallStatus = status; break }
          }
          batchOverrides[codigoHinca] = status
        }
        // One localStorage write
        batchSavePileStatusOverrides(batchOverrides)
        // One GeoJSON rebuild
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ;(mapRef.current?.getSource('piles') as maplibregl.GeoJSONSource)?.setData(buildPilesGeoJSON(trackers) as any)
      } else {
        const batchOverrides: Record<string, { status: TrackerStatus; progressPct: number }> = {}
        for (const codigoTracker of selectedElementIds) {
          for (const trackers of dataset.trackersByParcela.values()) {
            const t = trackers.find(x => x.codigoTracker === codigoTracker)
            if (t) { t.status = status; batchOverrides[codigoTracker] = { status, progressPct: t.progressPct }; break }
          }
        }
        // One localStorage write
        batchSaveTrackerOverrides(batchOverrides)
        // One GeoJSON rebuild
        if (selectedParcela !== null) {
          const trackers = dataset.trackersByParcela.get(selectedParcela) ?? []
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ;(mapRef.current?.getSource('trackers') as maplibregl.GeoJSONSource)?.setData(buildTrackerGeoJSON(trackers) as any)
        }
      }
      setIsBatchProcessing(false)
      setSelectedElementIds(new Set())
      setBatchEditOpen(false)
      setBatchBarPos(null)
      clearSelectionVisuals()
    }, 0)
  }, [dataset, selectedElementIds, activeElement, selectedParcela, clearSelectionVisuals])

  // Batch tracker step: advance all selected trackers to the chosen WBS step (single rebuild)
  const handleBatchTrackerStep = useCallback((stepCode: TrackerStepCode) => {
    if (!dataset || selectedElementIds.size === 0) return
    const pct = trackerStepStartPct(stepCode)
    const status: TrackerStatus = pct === 0 ? 'NO_INICIADA' : 'EN_CURSO'
    setIsBatchProcessing(true)
    setTimeout(() => {
      const batchOverrides: Record<string, { status: TrackerStatus; progressPct: number }> = {}
      for (const codigoTracker of selectedElementIds) {
        for (const trackers of dataset.trackersByParcela.values()) {
          const t = trackers.find(x => x.codigoTracker === codigoTracker)
          if (t) { t.status = status; t.progressPct = pct; batchOverrides[codigoTracker] = { status, progressPct: pct }; break }
        }
      }
      batchSaveTrackerOverrides(batchOverrides)
      if (selectedParcela !== null) {
        const trackers = dataset.trackersByParcela.get(selectedParcela) ?? []
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ;(mapRef.current?.getSource('trackers') as maplibregl.GeoJSONSource)?.setData(buildTrackerGeoJSON(trackers) as any)
      }
      setIsBatchProcessing(false)
      setSelectedElementIds(new Set())
      setBatchEditOpen(false)
      setBatchBarPos(null)
      clearSelectionVisuals()
    }, 0)
  }, [dataset, selectedElementIds, selectedParcela, clearSelectionVisuals])

  // Batch pile step: set all selected piles to EN_CURSO at the chosen WBS step (single rebuild)
  const handleBatchPileStep = useCallback((stepCode: string) => {
    if (!dataset || selectedElementIds.size === 0 || selectedParcela === null) return
    setIsBatchProcessing(true)
    setTimeout(() => {
      const trackers = dataset.trackersByParcela.get(selectedParcela) ?? []
      const batchOverrides: Record<string, TrackerStatus> = {}
      for (const codigoHinca of selectedElementIds) {
        for (const tracker of trackers) {
          const pile = tracker.piles.find(p => p.codigoHinca === codigoHinca)
          if (pile) { pile.overallStatus = 'EN_CURSO'; break }
        }
        batchOverrides[codigoHinca] = 'EN_CURSO'
        void stepCode  // step code stored for future backend integration
      }
      batchSavePileStatusOverrides(batchOverrides)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(mapRef.current?.getSource('piles') as maplibregl.GeoJSONSource)?.setData(buildPilesGeoJSON(trackers) as any)
      setIsBatchProcessing(false)
      setSelectedElementIds(new Set())
      setBatchEditOpen(false)
      setBatchBarPos(null)
      clearSelectionVisuals()
    }, 0)
  }, [dataset, selectedElementIds, selectedParcela, clearSelectionVisuals])

  // Reliable canvas coordinates — always relative to map container top-left
  const getCanvasXY = (e: React.MouseEvent) => {
    const rect = mapContainerRef.current!.getBoundingClientRect()
    return { x: e.clientX - rect.left, y: e.clientY - rect.top }
  }

  // Box selection mouse handlers
  const handleSelectionMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!selectionMode) return
    isDraggingRef.current = true
    const { x, y } = getCanvasXY(e)
    selectionStartRef.current = { x, y }
    setSelectionBox(null)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionMode])

  const handleSelectionMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!selectionMode || !isDraggingRef.current || !selectionStartRef.current) return
    const { x, y } = getCanvasXY(e)
    setSelectionBox({ x1: selectionStartRef.current.x, y1: selectionStartRef.current.y, x2: x, y2: y })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionMode])

  const handleSelectionMouseUp = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current) return
    isDraggingRef.current = false

    const map = mapRef.current
    const startPt = selectionStartRef.current
    if (!map || !startPt) { setSelectionBox(null); return }

    const cur = getCanvasXY(e)
    const x1 = Math.min(startPt.x, cur.x)
    const y1 = Math.min(startPt.y, cur.y)
    const x2 = Math.max(startPt.x, cur.x)
    const y2 = Math.max(startPt.y, cur.y)

    // Single click (≤5 px movement) in hincas/motoras Solo mode → QuickEditPopover
    if (Math.abs(x2 - x1) <= 5 && Math.abs(y2 - y1) <= 5 &&
        (activeElement === 'hincas' || activeElement === 'motoras') &&
        dataset && selectedParcela !== null) {
      const features = map.queryRenderedFeatures([startPt.x, startPt.y], { layers: ['piles-circle', 'motor-piers'] })
      const feature = features[0]
      if (feature) {
        const codigoHinca   = feature.properties?.codigoHinca   as string
        const codigoTracker = feature.properties?.codigoTracker as string
        const posicion      = feature.properties?.posicion      as number
        const trackers = dataset.trackersByParcela.get(selectedParcela) ?? []
        for (const tracker of trackers) {
          const pile = tracker.piles.find(p => p.codigoHinca === codigoHinca)
          if (pile) {
            setQuickEdit({
              type: 'pile', x: startPt.x, y: startPt.y,
              codigoTracker, codigoHinca, posicion,
              currentPartNo:  pile.partNo,
              currentStatus:  pile.overallStatus,
              esMotora:       pile.esMotora,
              tieneDamper:    pile.tieneDamper,
            })
            break
          }
        }
      }
      setSelectionBox(null)
      selectionStartRef.current = null
      return
    }

    const isDrag = Math.abs(x2 - x1) > 5 || Math.abs(y2 - y1) > 5

    if (!isDrag) {
      // Solo click (no drag) — open QuickEditPopover for hinca/motora under cursor
      const features = map.queryRenderedFeatures([cur.x, cur.y], { layers: ['piles-circle', 'motor-piers'] })
      const feature = features[0]
      if (feature && dataset) {
        const codigoHinca = feature.properties?.codigoHinca as string
        const posicion    = feature.properties?.posicion    as number
        const parcelaNum    = parseInt(String(feature.properties?.parcela ?? 0))
        const searchIn: TrackerData[][] = parcelaNum
          ? [dataset.trackersByParcela.get(parcelaNum) ?? []]
          : [...dataset.trackersByParcela.values()]
        let pile = null
        let foundTracker = null
        outer: for (const list of searchIn) {
          for (const t of list) {
            const p = t.piles.find(pp => pp.codigoHinca === codigoHinca)
            if (p) { pile = p; foundTracker = t; break outer }
          }
        }
        if (pile && foundTracker) {
          setQuickEdit({
            type: 'pile', x: cur.x, y: cur.y,
            codigoTracker: foundTracker.codigoTracker, codigoHinca, posicion,
            currentPartNo:  pile.partNo,
            currentStatus:  pile.overallStatus,
            esMotora:       pile.esMotora,
            tieneDamper:    pile.tieneDamper,
          })
        }
      }
      setSelectionBox(null)
      selectionStartRef.current = null
      return
    }

    if (isDrag) {
      const cfg = ELEMENT_TYPES.find(et => et.key === (activeElement ?? 'trackers')) ?? ELEMENT_TYPES[0]
      const queryLayers = cfg.queryIds.length > 0 ? cfg.queryIds : ['trackers-line']
      const prop        = cfg.featureProp || 'codigoTracker'

      const features = map.queryRenderedFeatures([[x1, y1], [x2, y2]], { layers: queryLayers })
      const ids = new Set<string>()

      // Clear previous highlights first
      clearSelectionVisuals()

      if (activeElement === 'hincas' || activeElement === 'motoras') {
        // Collect IDs and build GeoJSON for the bright-ring highlight layer
        const highlightFeatures: { type: 'Feature'; geometry: { type: 'Point'; coordinates: number[] }; properties: { id: string } }[] = []
        features.forEach(f => {
          const id = f.properties?.[prop] as string
          if (!id) return
          ids.add(id)
          if (dataset && selectedParcela !== null) {
            const trackers = dataset.trackersByParcela.get(selectedParcela) ?? []
            for (const tracker of trackers) {
              const pile = tracker.piles.find(p => p.codigoHinca === id)
              if (pile) {
                highlightFeatures.push({ type: 'Feature', geometry: { type: 'Point', coordinates: [pile.lng, pile.lat] }, properties: { id } })
                break
              }
            }
          }
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ;(map.getSource('piles-selected') as maplibregl.GeoJSONSource)?.setData({ type: 'FeatureCollection', features: highlightFeatures } as any)
      } else {
        // Tracker selection — use feature-state for highlight (bright/wide line)
        const newMapIds: number[] = []
        features.forEach(f => {
          const id = f.properties?.[prop] as string
          if (!id) return
          ids.add(id)
          if (typeof f.id === 'number') {
            newMapIds.push(f.id)
            map.setFeatureState({ source: 'trackers', id: f.id }, { selected: true })
          }
        })
        selectedTrackerMapIdsRef.current = newMapIds
      }

      setSelectedElementIds(ids)
      if (ids.size > 0) {
        // Position bar just below the center of the selection rectangle
        setBatchBarPos({ x: (x1 + x2) / 2, y: y2 + 14 })
        setBatchEditOpen(true)
      } else {
        setBatchEditOpen(false)
      }
    }

    setSelectionBox(null)
    selectionStartRef.current = null
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeElement, dataset, selectedParcela, clearSelectionVisuals])


  const zoomIn  = () => mapRef.current?.zoomIn()
  const zoomOut = () => mapRef.current?.zoomOut()
  const fitAll  = () => {
    if (!dataset || !mapRef.current) return
    mapRef.current.fitBounds([[dataset.bounds[0], dataset.bounds[1]], [dataset.bounds[2], dataset.bounds[3]]], { padding: 60, duration: 1000 })
  }

  const parcelaStats = selectedParcela !== null ? dataset?.parcelaStats.get(selectedParcela) : null

  // ==========================================================================
  return (
    <div className="flex h-full w-full bg-[#0F172A] text-white overflow-hidden">

      {/* LEFT PANEL */}
      <aside className="w-72 shrink-0 h-full flex flex-col bg-[#1E293B] border-r border-slate-700 overflow-y-auto" style={{ minHeight: 0 }}>

        <div className="flex items-center gap-3 px-4 py-4 border-b border-slate-700">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-400 shrink-0">
            <Sun className="w-4 h-4 text-amber-900" />
          </div>
          <div>
            <p className="text-sm font-bold text-white leading-tight">Layout Planta</p>
            <p className="text-xs text-slate-400 leading-tight">PSFV Navabuena</p>
          </div>
        </div>

        <div className="mx-3 my-2 px-3 py-2 bg-amber-500/10 border border-amber-500/20 rounded-lg">
          <div className="flex items-start gap-2">
            <Edit3 className="w-3 h-3 text-amber-400 shrink-0 mt-0.5" />
            <p className="text-[10px] text-amber-300 leading-relaxed">
              <strong>1 clic</strong> selecciona · <strong>2 clics</strong> edita estado<br/>
              <strong className="text-blue-300">⬛ Selección</strong> para cambio masivo
            </p>
          </div>
        </div>

        {dataset && (
          <div className="px-4 py-3 border-b border-slate-700">
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-[#0F172A] rounded-lg p-2.5 text-center">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">Trackers</p>
                <p className="text-lg font-bold text-white">{dataset.totalTrackers.toLocaleString()}</p>
              </div>
              <div className="bg-[#0F172A] rounded-lg p-2.5 text-center">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">Hincas</p>
                <p className="text-lg font-bold text-white">{dataset.totalPiles.toLocaleString()}</p>
              </div>
            </div>
          </div>
        )}

        {parcelaStats && (
          <div className="px-4 py-3 border-b border-slate-700">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">
              Parcela {String(selectedParcela).padStart(2, '0')} — Resumen
            </p>
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-slate-400">Avance global</span>
                <span className="text-sm font-bold text-amber-400">{parcelaStats.progressPct}%</span>
              </div>
              <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                <div className="h-full rounded-full"
                     style={{ width: `${parcelaStats.progressPct}%`, background: 'linear-gradient(90deg,#F59E0B,#FCD34D)', boxShadow: '0 0 8px #F59E0B80' }} />
              </div>
            </div>
            <div className="space-y-1">
              <ParcelaStatBar label="No iniciada" value={parcelaStats.noIniciada} color="#475569" />
              <ParcelaStatBar label="En curso"    value={parcelaStats.enCurso}    color="#F59E0B" />
              <ParcelaStatBar label="Finalizada"  value={parcelaStats.finalizada} color="#22C55E" />
              <ParcelaStatBar label="Bloqueada"   value={parcelaStats.bloqueada}  color="#EF4444" />
            </div>
          </div>
        )}

        {/* Parcela grid */}
        <div className="px-4 py-3 border-b border-slate-700">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Parcelas</p>
            {selectedParcela !== null && (
              <button onClick={() => setSelectedParcela(null)} className="text-[10px] text-slate-500 hover:text-slate-300 underline">Limpiar</button>
            )}
          </div>
          <div className="grid grid-cols-5 gap-1.5">
            {Array.from({ length: 26 }, (_, i) => i + 1).map((num) => {
              const stats = dataset?.parcelaStats.get(num)
              const pct   = stats?.progressPct ?? 0
              const isSel = selectedParcela === num
              const dotColor = !stats ? '#374151' : pct === 100 ? '#22C55E' : pct > 50 ? '#4ADE80' : pct > 0 ? '#F59E0B' : '#475569'
              return (
                <button key={num}
                        onClick={() => dataset ? setSelectedParcela(num === selectedParcela ? null : num) : undefined}
                        disabled={!dataset}
                        className={`relative flex flex-col items-center justify-center h-10 rounded-lg text-xs font-semibold border transition-all duration-200
                          ${isSel ? 'border-amber-500 text-white' : 'border-slate-700 text-slate-400 hover:border-slate-500 hover:text-slate-200'}
                          ${!dataset ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                        style={isSel ? { backgroundColor: '#F59E0B18', boxShadow: '0 0 12px #F59E0B40' } : undefined}>
                  <span className="leading-none">{String(num).padStart(2, '0')}</span>
                  {stats && <div className="absolute bottom-1 w-3 h-0.5 rounded-full" style={{ backgroundColor: dotColor }} />}
                </button>
              )
            })}
          </div>
        </div>

        {/* Element types — comprehensive layer manager */}
        <div className="px-3 py-3 border-b border-slate-700">
          <div className="flex items-center justify-between mb-2 px-1">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">Elementos de la planta</p>
            {activeElement && (
              <button onClick={deIsolate} className="text-[9px] text-blue-400 hover:text-blue-200 transition-colors font-medium">
                ✕ Salir selección
              </button>
            )}
          </div>

          {/* Loaded + selectable: show "Solo" button to isolate and activate box-selection */}
          {!activeElement && (
            <div className="mb-2 px-1 py-1 bg-slate-800/40 rounded-lg border border-slate-700/40">
              <p className="text-[9px] text-slate-500 leading-relaxed">
                Pulsa <strong className="text-slate-400">Solo</strong> en Trackers o Hincas para aislar la capa y activar selección múltiple.
              </p>
            </div>
          )}
          {activeElement && (
            <div className="mb-2 px-2 py-1.5 bg-blue-500/8 border border-blue-500/25 rounded-lg">
              <p className="text-[9px] text-blue-300 leading-relaxed">
                <strong>Arrastra un rectángulo</strong> en el mapa para seleccionar. Los seleccionados se resaltan en azul.
              </p>
            </div>
          )}

          <div className="space-y-0.5">
            {ELEMENT_TYPES.map((et) => {
              const isVisible = visibleElements[et.key] ?? true
              const isActive  = activeElement === et.key
              const Icon      = et.icon
              return (
                <div
                  key={et.key}
                  className={`flex items-center gap-2 px-2 py-1.5 rounded-lg transition-all duration-150 border
                    ${isActive
                      ? 'bg-blue-500/10 border-blue-500/40'
                      : 'border-transparent'
                    } ${!et.loaded ? 'opacity-35' : ''}`}
                >
                  {/* Visibility toggle (icon) */}
                  <button
                    onClick={() => et.loaded && !isActive && setVisibleElements(p => ({ ...p, [et.key]: !p[et.key] }))}
                    className={`w-5 h-5 rounded flex items-center justify-center shrink-0 transition-colors
                      ${!et.loaded || isActive ? 'text-slate-600 cursor-not-allowed' : isVisible ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-400'}`}
                    title={!et.loaded ? 'No cargado' : isActive ? 'Capa aislada' : isVisible ? 'Ocultar capa' : 'Mostrar capa'}
                  >
                    <Icon className="w-3 h-3" />
                  </button>

                  {/* Label */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: et.color }} />
                      <span className={`text-xs font-medium truncate ${
                        !et.loaded ? 'text-slate-600'
                        : isActive  ? 'text-blue-300'
                        : isVisible ? 'text-slate-200'
                        : 'text-slate-500'
                      }`}>{et.label}</span>
                    </div>
                    <p className="text-[9px] text-slate-600 truncate pl-3">{et.sublabel}</p>
                  </div>

                  {/* Right: "Solo" button for selectable loaded types, or "Pendiente" badge */}
                  {!et.loaded ? (
                    <span className="text-[8px] text-slate-700 uppercase tracking-wide shrink-0">Pdte.</span>
                  ) : et.selectable ? (
                    <button
                      onClick={() => isActive ? deIsolate() : isolateElement(et.key)}
                      className={`px-1.5 py-0.5 rounded text-[8px] font-bold shrink-0 transition-all border ${
                        isActive
                          ? 'bg-blue-500/20 text-blue-300 border-blue-500/50 shadow-[0_0_6px_#3B82F640]'
                          : 'bg-slate-700/70 text-slate-400 hover:text-white hover:bg-slate-600 border-slate-600/60'
                      }`}
                    >
                      {isActive ? 'Solo ✕' : 'Solo'}
                    </button>
                  ) : null}
                </div>
              )
            })}
          </div>
        </div>

        {/* Status legend */}
        <div className="px-4 py-3">
          <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">Estado</p>
          <div className="space-y-1.5 mb-3">
            {(Object.entries(TRACKER_STATUS_META) as [TrackerStatus, typeof TRACKER_STATUS_META[TrackerStatus]][]).map(([, meta]) => (
              <div key={meta.label} className="flex items-center gap-2">
                <div className="w-4 h-1 rounded-full" style={{ backgroundColor: meta.color, boxShadow: `0 0 4px ${meta.glowColor}` }} />
                <span className="text-xs text-slate-400">{meta.label}</span>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1.5">Indicadores hincas</p>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-transparent border-2 border-[#FCD34D]" />
              <span className="text-[10px] text-slate-400">Hinca con DAMPER</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-[#1E40AF]" />
              <span className="text-[10px] text-slate-400">Motora (tamaño mayor)</span>
            </div>
          </div>
        </div>
      </aside>

      {/* MAP */}
      <div className="flex-1 relative overflow-hidden">

        {/* Loading overlay */}
        {!dataset && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-[#0F172A]">
            {loadError ? (
              <div className="text-center">
                <AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-3" />
                <p className="text-red-300 font-semibold mb-1">Error al cargar datos</p>
                <p className="text-sm text-slate-500 max-w-xs">{loadError}</p>
              </div>
            ) : (
              <div className="text-center">
                <div className="relative w-16 h-16 mx-auto mb-6">
                  <div className="absolute inset-0 rounded-full border-4 border-slate-700" />
                  <div className="absolute inset-0 rounded-full border-4 border-t-amber-400 border-r-transparent border-b-transparent border-l-transparent animate-spin" style={{ animationDuration: '1s' }} />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xs font-bold text-amber-400">{loadProgress}%</span>
                  </div>
                </div>
                <p className="text-white font-semibold mb-1">Cargando datos de planta</p>
                <p className="text-sm text-slate-500">
                  {loadProgress < 10 ? 'Descargando CSV…' : loadProgress < 85 ? 'Procesando coordenadas UTM…' : loadProgress < 92 ? 'Construyendo trackers…' : 'Calculando estadísticas…'}
                </p>
                <div className="mt-4 w-64 h-1 bg-slate-800 rounded-full overflow-hidden">
                  <div className="h-full bg-amber-400 rounded-full transition-all duration-300" style={{ width: `${loadProgress}%`, boxShadow: '0 0 8px #F59E0B' }} />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Global view hint — shown when data is loaded and no parcela is selected */}
        {dataset && !selectedParcela && (
          <div className="absolute bottom-16 left-1/2 -translate-x-1/2 z-10 pointer-events-none">
            <div className="bg-[#1E293B]/80 backdrop-blur-sm border border-slate-600/60 rounded-xl px-5 py-2.5 text-center shadow-xl">
              <p className="text-xs text-slate-400">
                Clic en un <span className="text-amber-400 font-semibold">perímetro</span> o elige una parcela en el panel lateral para ver el detalle
              </p>
            </div>
          </div>
        )}

        {/* MapLibre container — no mouse handlers here (MapLibre swallows events) */}
        <div
          ref={mapContainerRef}
          className="absolute inset-0"
          onDoubleClick={(e) => e.preventDefault()}
        />

        {/* Transparent selection overlay — rendered ABOVE the MapLibre canvas only
            when selection mode is active, so our React mouse events fire correctly */}
        {selectionMode && (
          <div
            className="absolute inset-0"
            style={{ zIndex: 6, cursor: 'crosshair' }}
            onMouseDown={handleSelectionMouseDown}
            onMouseMove={handleSelectionMouseMove}
            onMouseUp={handleSelectionMouseUp}
            onMouseLeave={() => {
              if (isDraggingRef.current) {
                isDraggingRef.current = false
                setSelectionBox(null)
                selectionStartRef.current = null
              }
            }}
          />
        )}

        {/* Selection rectangle overlay */}
        {selectionBox && (
          <div
            className="absolute pointer-events-none z-10"
            style={{
              left:   Math.min(selectionBox.x1, selectionBox.x2),
              top:    Math.min(selectionBox.y1, selectionBox.y2),
              width:  Math.abs(selectionBox.x2 - selectionBox.x1),
              height: Math.abs(selectionBox.y2 - selectionBox.y1),
              border: '2px solid #60A5FA',
              backgroundColor: 'rgba(96, 165, 250, 0.08)',
              boxShadow: '0 0 0 1px rgba(96, 165, 250, 0.3)',
            }}
          />
        )}

        {/* Quick Edit Popover */}
        {quickEdit && (
          <QuickEditPopover
            edit={quickEdit}
            mapWidth={mapSize.w}
            mapHeight={mapSize.h}
            onClose={() => setQuickEdit(null)}
            onStatusChange={handleTrackerStatusChange}
            onPileStatusChange={handlePileStatusChange}
            onGenericStatusChange={(featureId, elementKey, stepCode) => {
              saveGenericStepOverride(elementKey, featureId, stepCode)
              persistToSupabase(elementKey, featureId, stepCode)
              setSelectedGeneric(prev => prev?.featureId === featureId ? { ...prev, stepCode } : prev)
              updateGenericFeatureStatus(elementKey, featureId, stepCode)
            }}
          />
        )}

        {/* Toolbar — top right */}
        <div className="absolute top-4 right-4 flex flex-col gap-1 z-10">
          <button onClick={zoomIn}  className="w-9 h-9 bg-[#1E293B]/90 backdrop-blur-sm rounded-t-xl border border-slate-600 flex items-center justify-center text-slate-300 hover:text-white hover:bg-[#334155] transition-colors"><ZoomIn className="w-4 h-4" /></button>
          <button onClick={zoomOut} className="w-9 h-9 bg-[#1E293B]/90 backdrop-blur-sm border-x border-b border-slate-600 flex items-center justify-center text-slate-300 hover:text-white hover:bg-[#334155] transition-colors"><ZoomOut className="w-4 h-4" /></button>
          <button onClick={fitAll}  className="w-9 h-9 bg-[#1E293B]/90 backdrop-blur-sm border-x border-b border-slate-600 flex items-center justify-center text-slate-300 hover:text-white hover:bg-[#334155] transition-colors" title="Encuadrar planta"><Maximize2 className="w-4 h-4" /></button>
          {/* Selection mode toggle — shortcut: isolates trackers by default */}
          <button
            onClick={() => selectionMode ? deIsolate() : isolateElement(activeElement ?? 'trackers')}
            title={selectionMode ? 'Salir de selección múltiple' : 'Activar selección múltiple (aisla la capa activa o Trackers)'}
            className={`w-9 h-9 backdrop-blur-sm rounded-b-xl border-x border-b flex items-center justify-center transition-all ${
              selectionMode
                ? 'bg-blue-600 border-blue-400 text-white shadow-[0_0_12px_#3B82F660]'
                : 'bg-[#1E293B]/90 border-slate-600 text-slate-300 hover:text-white hover:bg-[#334155]'
            }`}
          >
            <BoxSelect className="w-4 h-4" />
          </button>
        </div>

        {/* Zoom indicator */}
        <div className="absolute bottom-8 right-4 z-10">
          <div className="bg-[#1E293B]/90 backdrop-blur-sm border border-slate-600 rounded-lg px-2.5 py-1 text-xs text-slate-400">z {zoom.toFixed(1)}</div>
        </div>

        {/* Parcela info bar */}
        {selectedParcela && parcelaStats && (
          <div className="absolute top-4 left-4 z-10">
            <div className="bg-[#1E293B]/90 backdrop-blur-sm border border-slate-600 rounded-xl px-4 py-2.5 flex items-center gap-4 shadow-xl">
              <button
                onClick={() => setSelectedParcela(null)}
                className="flex items-center gap-1.5 text-slate-400 hover:text-white transition-colors text-xs border-r border-slate-600 pr-3"
                title="Volver a vista general de planta"
              >
                ← Planta
              </button>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                <span className="font-semibold text-white text-sm">Parcela {String(selectedParcela).padStart(2, '0')}</span>
              </div>
              <div className="flex items-center gap-3 text-xs text-slate-400 border-l border-slate-600 pl-4">
                <span className="flex items-center gap-1"><Activity className="w-3 h-3 text-amber-400" />{parcelaStats.totalTrackers} trackers</span>
                <span className="flex items-center gap-1"><BarChart2 className="w-3 h-3 text-green-400" />{parcelaStats.progressPct}%</span>
              </div>
            </div>
          </div>
        )}

        {/* Batch edit bar — appears near where selection ended */}
        {batchEditOpen && selectedElementIds.size > 0 && batchBarPos && (
          <div
            className="absolute z-20"
            style={{
              left:      batchBarPos.x,
              top:       Math.min(batchBarPos.y, mapSize.h - 72),
              transform: 'translateX(-50%)',
            }}
          >
            <div className="relative bg-[#0F172A] border border-blue-500/50 rounded-2xl px-5 py-3 flex items-center gap-4 shadow-2xl"
                 style={{ boxShadow: '0 0 30px #3B82F640, 0 4px 24px #00000080' }}>
              {/* Batch processing overlay */}
              {isBatchProcessing && (
                <div className="absolute inset-0 z-30 flex items-center justify-center bg-[#0F172A]/90 rounded-2xl gap-2">
                  <div className="w-4 h-4 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm font-semibold text-amber-400">Aplicando cambios…</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
                <span className="text-white font-semibold text-sm">
                  {selectedElementIds.size} {ELEMENT_TYPES.find(e => e.key === activeElement)?.label ?? 'elementos'} seleccionados
                </span>
              </div>
              <div className="flex gap-1.5 border-l border-slate-700 pl-4 flex-wrap max-w-lg">
                {getStepsForElementKey(activeElement ?? 'trackers').map((step) => (
                  <button
                    key={step.code}
                    onClick={() =>
                      activeElement === 'hincas' || activeElement === 'motoras'
                        ? handleBatchPileStep(step.code)
                        : handleBatchTrackerStep(step.code as TrackerStepCode)
                    }
                    className="px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-all hover:scale-105 whitespace-nowrap"
                    style={{
                      backgroundColor: `${step.color}20`,
                      color: step.glowColor,
                      border: `1px solid ${step.color}60`,
                      boxShadow: `0 0 6px ${step.color}25`,
                    }}
                    title={`${step.code} — ${step.label}`}
                  >
                    <span className="text-[9px] opacity-60 mr-1">{step.code}</span>
                    {step.shortLabel}
                  </button>
                ))}
                <div className="w-px bg-slate-700 mx-0.5" />
                {(['NO_INICIADA', 'BLOQUEADA', 'FINALIZADA'] as TrackerStatus[]).map(status => {
                  const meta = TRACKER_STATUS_META[status]
                  return (
                    <button key={status}
                            onClick={() => handleBatchStatusChange(status)}
                            className="px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-all hover:scale-105"
                            style={{ backgroundColor: `${meta.color}20`, color: meta.glowColor, border: `1px solid ${meta.color}60` }}>
                      {meta.label}
                    </button>
                  )
                })}
              </div>
              <button onClick={() => { clearSelectionVisuals(); setSelectedElementIds(new Set()); setBatchEditOpen(false); setBatchBarPos(null) }}
                      className="text-slate-500 hover:text-white transition-colors ml-1"
                      title="Limpiar selección (mantiene capa aislada)">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* RIGHT PANEL — Tracker or Generic element detail */}
      <div className={`absolute right-0 top-0 bottom-0 z-20 flex flex-col bg-[#1E293B] border-l border-slate-700 shadow-2xl transition-transform duration-300 ease-in-out w-80 ${(detailOpen || selectedGeneric !== null) ? 'translate-x-0' : 'translate-x-full'}`}>
        {selectedTracker && detailOpen && (
          <TrackerDetailPanel
            tracker={selectedTracker}
            onClose={() => {
              setDetailOpen(false)
              setSelectedTracker(null)
              if (selectedIdRef.current !== null) {
                mapRef.current?.setFeatureState({ source: 'trackers', id: selectedIdRef.current }, { selected: false })
                selectedIdRef.current = null
              }
            }}
            onSave={handleTrackerSave}
            onPileStatusChange={handlePileStatusChange}
          />
        )}
        {selectedGeneric && (
          <GenericElementPanel
            layerKey={selectedGeneric.layerKey}
            layerLabel={selectedGeneric.layerLabel}
            featureId={selectedGeneric.featureId}
            steps={selectedGeneric.steps}
            initialStepCode={selectedGeneric.stepCode}
            layerColor={selectedGeneric.layerColor}
            onClose={() => setSelectedGeneric(null)}
            onSave={handleGenericSave}
          />
        )}
      </div>
    </div>
  )
}
